import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, EyeOff, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';

export default function AddEmployeeModal({ isOpen, onClose, editData, onSuccess }) {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState({ departments: [], designations: [] });
  const [isNewDept, setIsNewDept] = useState(false);
  const [isNewDesig, setIsNewDesig] = useState(false);
  const [duplicateEmp, setDuplicateEmp] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'Employee',
    department: '',
    designation: '',
    salary: '',
    joining_date: '',
    shift_start_time: '',
    shift_end_time: '',
    address: ''
  });
  const [photo, setPhoto] = useState(null);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const res = await api.get('/employee-options');
        setOptions(res.data);
      } catch (e) {
        console.error("Failed to fetch options", e);
      }
    };
    fetchOptions();

    if (isOpen) {
      setIsNewDept(false);
      setIsNewDesig(false);
      if (editData) {
        setFormData({
          name: editData.name !== 'Unknown Employee' ? editData.name : '',
          email: editData.user?.email || '',
          phone: editData.user?.phone || '',
          password: '',
          role: editData.user?.role || 'Employee',
          department: editData.department || '',
          designation: editData.role && editData.role !== 'N/A' ? editData.role : (editData.designation || ''),
          salary: editData.salary || '',
          joining_date: editData.joining_date || '',
          shift_start_time: editData.shift_start_time || '',
          shift_end_time: editData.shift_end_time || '',
          address: editData.address || ''
        });
      } else {
        setFormData({
          name: '', email: '', phone: '', password: '', role: 'Employee', department: '', designation: '', salary: '', joining_date: '', shift_start_time: '', shift_end_time: '', address: ''
        });
      }
      setPhoto(null);
      setDuplicateEmp(null);
    }
  }, [isOpen, editData]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    setPhoto(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      });
      
      if (photo) submitData.append('profile_photo', photo);

      if (editData) {
        submitData.append('_method', 'PUT'); // Laravel requirement for FormData PUT
        await api.post(`/employees/${editData.id}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Employee updated successfully');
      } else {
        if (!formData.password) {
          toast.error("Password is required for new employees.");
          setLoading(false);
          return;
        }
        await api.post('/employees', submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Employee created successfully');
      }
      
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error(err);
      if (err.response?.data?.existing_employee) {
        setDuplicateEmp(err.response.data.existing_employee);
      } else {
        toast.error(err.response?.data?.message || 'Failed to save employee. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={onClose}></div>
      
      {/* Modal Content */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[96vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50/50">
          <h2 className="text-lg font-bold text-slate-800">
            {editData ? 'Edit Employee Details' : 'Add New Employee'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1">
            <X size={20} />
          </button>
        </div>

        {/* Duplicate Employee Alert Popup */}
        {duplicateEmp && (
          <div className="absolute inset-0 z-[100] bg-white/95 backdrop-blur-sm flex items-center justify-center p-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="bg-white border-2 border-red-500 rounded-2xl shadow-2xl p-6 w-full max-w-lg">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center border-4 border-red-50">
                  <span className="text-2xl">⚠️</span>
                </div>
              </div>
              <h3 className="text-xl font-black text-center text-slate-800 mb-2">Duplicate Record Found!</h3>
              <p className="text-sm font-semibold text-center text-red-600 mb-6 bg-red-50/50 py-2 rounded-lg border border-red-100">
                An employee is already registered with this <br/><span className="font-black text-red-700">Email or Phone Number</span>.
              </p>
              
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-16 h-16 bg-red-500 opacity-5 rounded-bl-full"></div>
                <div className="grid grid-cols-2 gap-y-3 text-sm">
                  <div>
                    <span className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-0.5">Emp ID</span>
                    <span className="font-bold text-slate-800 bg-white px-2 py-0.5 rounded shadow-sm border border-slate-100">{duplicateEmp.employee_id || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-0.5">Name</span>
                    <span className="font-bold text-slate-800">{duplicateEmp.name}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-0.5">Email</span>
                    <span className="font-bold text-slate-700">{duplicateEmp.email}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-0.5">Phone</span>
                    <span className="font-bold text-slate-700">{duplicateEmp.phone}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-0.5">Department</span>
                    <span className="font-bold text-slate-700">{duplicateEmp.department || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-0.5">Designation</span>
                    <span className="font-bold text-slate-700">{duplicateEmp.designation || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-center">
                <button
                  onClick={() => setDuplicateEmp(null)}
                  className="px-6 py-2.5 bg-red-500 hover:bg-red-600 text-white font-bold rounded-lg transition-all shadow-md active:scale-95"
                >
                  Close & Change Details
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Form Body - Scrollable */}
        <div className="p-5 overflow-y-auto flex-1 custom-scrollbar">
          <form id="employee-form" onSubmit={handleSubmit} className="space-y-4">
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-5 gap-y-4">
              {/* Full Name */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-600">Full Name <span className="text-red-500">*</span></label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-200 rounded-md text-[13px] focus:outline-none focus:ring-2 focus:ring-[#10b981]/20 focus:border-[#10b981] transition-all text-slate-800" />
              </div>

              {/* Email */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-600">Email Address <span className="text-red-500">*</span></label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-200 rounded-md text-[13px] focus:outline-none focus:ring-2 focus:ring-[#10b981]/20 focus:border-[#10b981] transition-all text-slate-800" />
              </div>

              {/* Phone Num */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-600">Phone Number <span className="text-red-500">*</span></label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-200 rounded-md text-[13px] focus:outline-none focus:ring-2 focus:ring-[#10b981]/20 focus:border-[#10b981] transition-all text-slate-800" />
              </div>

              {/* Role */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-600">User Role <span className="text-red-500">*</span></label>
                <select name="role" value={formData.role} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-200 rounded-md text-[13px] focus:outline-none focus:ring-2 focus:ring-[#10b981]/20 focus:border-[#10b981] transition-all text-slate-800 bg-white">
                  <option value="Employee">Employee</option>
                  <option value="Admin">Admin</option>
                </select>
              </div>

              {/* Department */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-600 flex justify-between items-center">
                  <span>Department <span className="text-red-500">*</span></span>
                  {isNewDept && <button type="button" onClick={() => { setIsNewDept(false); setFormData({...formData, department: ''}); }} className="text-[#10b981] text-[10px]">Cancel</button>}
                </label>
                {isNewDept ? (
                  <input type="text" name="department" value={formData.department} onChange={handleChange} placeholder="Type new department" required className="w-full px-3 py-2 border border-emerald-300 rounded-md text-[13px] focus:outline-none focus:ring-2 focus:ring-[#10b981]/20 focus:border-[#10b981] transition-all text-slate-800" autoFocus />
                ) : (
                  <select name="department" value={formData.department} onChange={(e) => { 
                    if(e.target.value === 'CREATE_NEW') { setIsNewDept(true); setFormData({...formData, department: ''}); } 
                    else { handleChange(e); }
                  }} required className="w-full px-3 py-2 border border-gray-200 rounded-md text-[13px] focus:outline-none focus:ring-2 focus:ring-[#10b981]/20 focus:border-[#10b981] transition-all text-slate-800 bg-white">
                    <option value="" disabled>Select Department</option>
                    {options.departments.map(d => <option key={d} value={d}>{d}</option>)}
                    <option value="CREATE_NEW" className="text-emerald-600 font-bold">+ Create New...</option>
                  </select>
                )}
              </div>

              {/* Designation */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-600 flex justify-between items-center">
                  <span>Designation <span className="text-red-500">*</span></span>
                  {isNewDesig && <button type="button" onClick={() => { setIsNewDesig(false); setFormData({...formData, designation: ''}); }} className="text-[#10b981] text-[10px]">Cancel</button>}
                </label>
                {isNewDesig ? (
                  <input type="text" name="designation" value={formData.designation} onChange={handleChange} placeholder="Type new designation" required className="w-full px-3 py-2 border border-emerald-300 rounded-md text-[13px] focus:outline-none focus:ring-2 focus:ring-[#10b981]/20 focus:border-[#10b981] transition-all text-slate-800" autoFocus />
                ) : (
                  <select name="designation" value={formData.designation} onChange={(e) => { 
                    if(e.target.value === 'CREATE_NEW') { setIsNewDesig(true); setFormData({...formData, designation: ''}); } 
                    else { handleChange(e); }
                  }} required className="w-full px-3 py-2 border border-gray-200 rounded-md text-[13px] focus:outline-none focus:ring-2 focus:ring-[#10b981]/20 focus:border-[#10b981] transition-all text-slate-800 bg-white">
                    <option value="" disabled>Select Designation</option>
                    {options.designations.map(d => <option key={d} value={d}>{d}</option>)}
                    <option value="CREATE_NEW" className="text-emerald-600 font-bold">+ Create New...</option>
                  </select>
                )}
              </div>

              {/* Salary */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-600">Salary (Monthly) <span className="text-red-500">*</span></label>
                <input type="number" name="salary" value={formData.salary} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-200 rounded-md text-[13px] focus:outline-none focus:ring-2 focus:ring-[#10b981]/20 focus:border-[#10b981] transition-all text-slate-800" />
              </div>

              {/* Joining Date */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-600">Joining Date <span className="text-red-500">*</span></label>
                <input type="date" name="joining_date" value={formData.joining_date} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-200 rounded-md text-[13px] focus:outline-none focus:ring-2 focus:ring-[#10b981]/20 focus:border-[#10b981] transition-all text-slate-600" />
              </div>

              {/* Password */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-600">
                  Password {editData ? <span className="text-gray-400 font-normal">(Leave blank to keep same)</span> : <span className="text-red-500">*</span>}
                </label>
                <div className="relative">
                  <input type={showPassword ? "text" : "password"} name="password" value={formData.password} onChange={handleChange} className="w-full pl-3 pr-9 py-2 border border-gray-200 rounded-md text-[13px] focus:outline-none focus:ring-2 focus:ring-[#10b981]/20 focus:border-[#10b981] transition-all text-slate-800" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                    <EyeOff size={15} />
                  </button>
                </div>
              </div>

              {/* Shift Start */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-600">Shift Start Time</label>
                <input type="time" name="shift_start_time" value={formData.shift_start_time} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-[13px] focus:outline-none focus:ring-2 focus:ring-[#10b981]/20 focus:border-[#10b981] transition-all text-slate-600" />
              </div>

              {/* Shift End */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-600">Shift End Time</label>
                <input type="time" name="shift_end_time" value={formData.shift_end_time} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-[13px] focus:outline-none focus:ring-2 focus:ring-[#10b981]/20 focus:border-[#10b981] transition-all text-slate-600" />
              </div>

              {/* Profile Photo */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-600">Profile Photo</label>
                <input type="file" accept="image/*" onChange={handleFileChange} className="w-full px-2.5 py-1.5 border border-gray-200 rounded-md text-[12px] focus:outline-none focus:ring-2 focus:ring-[#10b981]/20 focus:border-[#10b981] transition-all text-slate-600 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100" />
              </div>

              {/* Address (Span full width) */}
              <div className="space-y-1 md:col-span-3">
                <label className="text-xs font-bold text-slate-600">Home Address</label>
                <textarea name="address" value={formData.address} onChange={handleChange} rows="2" className="w-full px-3 py-2 border border-gray-200 rounded-md text-[13px] focus:outline-none focus:ring-2 focus:ring-[#10b981]/20 focus:border-[#10b981] transition-all text-slate-800 resize-y"></textarea>
              </div>

            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50/50 shrink-0">
          <button type="button" onClick={onClose} disabled={loading} className="px-4 py-2 text-sm font-bold text-slate-600 hover:bg-gray-200 bg-gray-100 rounded-md transition-colors disabled:opacity-50">
            Cancel
          </button>
          <button type="submit" form="employee-form" disabled={loading} className="px-5 py-2 bg-[#10b981] hover:bg-emerald-600 text-white text-sm font-bold rounded-md shadow-sm transition-colors flex items-center justify-center min-w-[120px] disabled:opacity-70">
            {loading ? <Loader2 size={16} className="animate-spin" /> : (editData ? 'Update Employee' : 'Create Employee')}
          </button>
        </div>

      </div>
    </div>,
    document.body
  );
}
